// Wrapper pra chamar Trigger.dev tasks via backend Bianca (api.abianca.com.br).
// Espelha o padrao do projeto bianca principal (src/services/backendApi.ts):
//  - POST /api/v1/tasks/{id}/trigger
//  - GET  /api/v3/runs/{runId} (polling)
//  - Retry em 5xx + network errors transientes
//  - Unwrap do output { json: ... } (SuperJSON)

// Em dev, usa proxy CRA (setupProxy.js) pra evitar CORS no localhost.
// Em prod, chama o backend direto (CORS ja liberado pra mfrealstate.com.br).
const BASE_URL = process.env.NODE_ENV === 'development'
  ? '/__bianca-api'
  : 'https://api.abianca.com.br';
const TRIGGER_TOKEN = process.env.REACT_APP_TRIGGER_PUBLIC_TOKEN || '';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 min
const MAX_TRIGGER_RETRIES = 3;

function authHeaders() {
  if (!TRIGGER_TOKEN) console.warn('[triggerApi] REACT_APP_TRIGGER_PUBLIC_TOKEN nao definido');
  return {
    Authorization: `Bearer ${TRIGGER_TOKEN}`,
    'Content-Type': 'application/json',
  };
}

export async function triggerTask(taskId, payload) {
  const body = JSON.stringify({ payload });
  for (let attempt = 0; attempt < MAX_TRIGGER_RETRIES; attempt++) {
    const res = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}/trigger`, {
      method: 'POST',
      headers: authHeaders(),
      body,
    });
    if (res.ok) {
      const data = await res.json();
      return { runId: data.id, publicAccessToken: data.publicAccessToken };
    }
    if ([502, 503, 504].includes(res.status) && attempt < MAX_TRIGGER_RETRIES - 1) {
      await new Promise(r => setTimeout(r, (attempt + 1) * 3000));
      continue;
    }
    const text = await res.text().catch(() => '');
    throw new Error(`Falha ao disparar ${taskId} (${res.status}): ${text}`);
  }
  throw new Error(`Maximo de tentativas excedido pra ${taskId}`);
}

const TERMINAL_STATUSES = new Set([
  'COMPLETED', 'CANCELED', 'FAILED', 'CRASHED', 'TIMED_OUT', 'INTERRUPTED', 'SYSTEM_FAILURE', 'EXPIRED',
]);

export async function triggerTaskAndWait(taskId, payload, { timeoutMs = POLL_TIMEOUT_MS, pollIntervalMs = POLL_INTERVAL_MS } = {}) {
  const { runId, publicAccessToken } = await triggerTask(taskId, payload);
  const pollToken = publicAccessToken || TRIGGER_TOKEN;
  const pollHeaders = pollToken ? { Authorization: `Bearer ${pollToken}` } : {};

  const deadline = Date.now() + timeoutMs;
  let consecutiveTransient = 0;

  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, pollIntervalMs));

    let res;
    try {
      res = await fetch(`${BASE_URL}/api/v3/runs/${runId}`, { headers: pollHeaders });
    } catch (err) {
      consecutiveTransient++;
      console.warn(`[triggerApi] poll ${runId} network err (${consecutiveTransient})`, err);
      if (consecutiveTransient >= 20) throw new Error(`Polling ${runId} falhou ${consecutiveTransient}x (network)`);
      continue;
    }

    if (res.status >= 500 && res.status < 600) {
      consecutiveTransient++;
      if (consecutiveTransient >= 20) throw new Error(`Polling ${runId} retornou ${res.status} ${consecutiveTransient}x`);
      continue;
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Erro consultando ${runId} (${res.status}): ${text}`);
    }

    consecutiveTransient = 0;

    let data;
    try { data = await res.json(); }
    catch {
      consecutiveTransient++;
      continue;
    }

    if (TERMINAL_STATUSES.has(data.status)) {
      const rawOutput = data.output;
      const unwrapped = (rawOutput && typeof rawOutput === 'object' && 'json' in rawOutput)
        ? rawOutput.json
        : rawOutput;
      if (data.status === 'COMPLETED') {
        return { ok: true, output: unwrapped, runId };
      }
      const errMsg = data.error?.message || data.error || unwrapped?.error || data.status;
      return { ok: false, error: errMsg, runId };
    }
  }
  return { ok: false, error: `Timeout aguardando ${taskId}`, runId };
}

export function humanizeTaskError(rawMsg) {
  if (!rawMsg) return 'Erro desconhecido. Tente novamente.';
  const msg = String(rawMsg);
  if (/unauthorized.*invalid token/i.test(msg)) return 'Sua sessao expirou. Faca login de novo.';
  if (/forbidden/i.test(msg)) return 'Voce nao tem permissao pra essa acao.';
  if (/corretor_nao_encontrado/i.test(msg)) return 'Corretor nao encontrado. Atualize a lista.';
  if (/status_invalido/i.test(msg)) return 'Este corretor ja foi processado. Atualize a lista.';
  if (/payload_invalido/i.test(msg)) return 'Dados invalidos enviados ao servidor.';
  if (/failed to fetch/i.test(msg)) return 'Nao consegui falar com o servidor. Verifique sua conexao ou CORS.';
  return msg;
}
